import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@ngneat/transloco';
import { firstValueFrom, forkJoin } from 'rxjs';
import {
  ACTIVE_LANGS,
  AppLang,
  DEFAULT_LANG,
  LANG_STORAGE_KEY,
  LanguageService,
  ParamsArg,
} from './language.service';
import { registerI18n } from './i18n';
import { TranslationKey } from './translation-keys';

/**
 * The Transloco-backed implementation of `LanguageService`.
 *
 * This is the ONLY file in `src/app/` that imports `@ngneat/transloco`, apart from the
 * composition root (`app.config.ts`) and the loader adapter (`core/transloco/`) — which is where
 * naming an engine is allowed. Everything else in the app injects the abstract `LanguageService`
 * and has no idea Transloco exists.
 */
@Injectable()
export class TranslocoLanguageService extends LanguageService {
  private readonly transloco = inject(TranslocoService);

  /** Scopes loaded at least once. All of them are re-loaded on every language switch. */
  private readonly loadedScopes = new Set<string>();

  /** Bridges Transloco's Observable API into a signal. Transloco has no signal API of its own. */
  private readonly activeLang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  override readonly currentLang: Signal<AppLang> = computed(() => this.normalize(this.activeLang()));
  override readonly availableLangs: Signal<readonly AppLang[]> = computed(() => ACTIVE_LANGS);

  constructor() {
    super();
    // Powers the DI-free t()/tEnum() used by *.constants.ts colDef closures, which run outside
    // any injection context.
    registerI18n(this);
  }

  override t<K extends TranslationKey>(key: K, ...args: ParamsArg<K>): string {
    return this.resolve(key, args[0] as Record<string, unknown> | undefined);
  }

  override resolve(key: string, params?: Record<string, unknown>): string {
    // NOT DEAD CODE. This read is what registers the calling view as a dependency of the language
    // signal — it is the entire reactivity mechanism behind translated grid headers and enum
    // cells. Remove it and every grid header goes stale on a language switch, with no error.
    this.currentLang();
    // Passthrough for already-resolved prose / backend text: a translation key is a
    // dot-separated identifier and NEVER contains whitespace, so a string with a space
    // cannot be a key. Returning it verbatim (instead of handing it to Transloco) renders
    // the exact same text while avoiding a spurious "Missing translation" console warning
    // from Transloco's missingHandler — this is resolve()'s documented passthrough intent.
    if (typeof key === 'string' && /\s/.test(key)) {
      return key;
    }
    return this.transloco.translate(key, params);
  }

  override translateDynamic(key: string, params?: Record<string, unknown>): string {
    return this.resolve(key, params);
  }

  override getCurrentLang(): AppLang {
    return this.currentLang();
  }

  override async setLanguage(lang: AppLang): Promise<void> {
    const next = this.normalize(lang);
    if (next === this.currentLang()) {
      return;
    }

    // Load BEFORE flipping, or the UI renders raw keys for a frame.
    await this.loadAllFor(next);
    this.transloco.setActiveLang(next);

    // Only an EXPLICIT choice is persisted. A device-derived language is never written — see
    // restore(): writing it would freeze the first language ever detected and stop consulting
    // the device from then on.
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      /* storage unavailable (private browsing) — the switch still applies for this session */
    }
  }

  override async useScope(scope: string): Promise<void> {
    this.loadedScopes.add(scope);
    await firstValueFrom(this.transloco.load(`${scope}/${this.currentLang()}`));
  }

  override restore(): AppLang {
    return this.storedChoice() ?? this.deviceLang() ?? DEFAULT_LANG;
  }

  /**
   * An explicit choice the user made on this device. Absent, corrupt or unsupported all yield
   * `undefined` so resolution continues — none of them is an error.
   */
  private storedChoice(): AppLang | undefined {
    try {
      return this.supported(localStorage.getItem(LANG_STORAGE_KEY));
    } catch {
      return undefined;
    }
  }

  /**
   * The device preference.
   *
   * `navigator.languages` is an ORDERED LIST, not a value. We walk it and take the first entry
   * whose PRIMARY SUBTAG we support: `['fr-FR', 'en-GB', 'es-ES']` resolves to `en`, not `es`.
   *
   * Two ways to get this wrong, both silent:
   *   - reading only `navigator.language` (the top preference) and falling back when it is
   *     unsupported — that serves Spanish to a user whose second preference is English;
   *   - comparing the full BCP-47 tag, since `'en-US'` is not a member of `['es', 'en']` — that
   *     serves Spanish to an English browser.
   */
  private deviceLang(): AppLang | undefined {
    const tags = navigator.languages?.length ? navigator.languages : [navigator.language];

    for (const tag of tags) {
      const hit = this.supported(tag?.split('-')[0]?.toLowerCase());
      if (hit) {
        return hit;
      }
    }

    return undefined;
  }

  private supported(lang: string | null | undefined): AppLang | undefined {
    // Only an ACTIVE language is supported. A browser set to a language this deployment does
    // not ship resolves to DEFAULT_LANG, which is correct: that language does not exist here.
    return (ACTIVE_LANGS as readonly string[]).includes(lang as string)
      ? (lang as AppLang)
      : undefined;
  }

  private async loadAllFor(lang: AppLang): Promise<void> {
    await firstValueFrom(
      forkJoin([
        this.transloco.load(lang),
        ...[...this.loadedScopes].map((scope) => this.transloco.load(`${scope}/${lang}`)),
      ]),
    );
  }

  private normalize(lang: string | null | undefined): AppLang {
    return this.supported(lang) ?? DEFAULT_LANG;
  }
}
